import { useEffect } from "react";
import { Badge, Card, CardBody, Col, FormGroup, Label, Row } from "reactstrap";
import WizardPanel from "../WizardPanel";
import { PanelField } from "../panelFields";
import { PRIMARY_HALO, SECTION_DIVIDER_STYLE } from "../wizardStyles";

/**
 * Classifies an IPv4 string. Returns "cgnat" for 100.64.0.0/10,
 * "public" for any other valid address, or null if missing / malformed.
 */
function detectCgnat(ip) {
  if (!ip) return null;
  const parts = ip.trim().split(".").map(Number);
  if (parts.length !== 4 || parts.some((p) => Number.isNaN(p) || p < 0 || p > 255)) return null;
  return parts[0] === 100 && parts[1] >= 64 && parts[1] <= 127 ? "cgnat" : "public";
}

function CgnatBadge({ wanIpv4 }) {
  const status = detectCgnat(wanIpv4);
  if (status === "cgnat") {
    return (
      <Badge color="danger" pill className="px-3 py-2">
        <i className="fas fa-exclamation-triangle mr-1" />
        CGNAT detected
      </Badge>
    );
  }
  if (status === "public") {
    return (
      <Badge color="success" pill className="px-3 py-2">
        <i className="fas fa-check-circle mr-1" />
        Public IP
      </Badge>
    );
  }
  return <span className="text-muted">—</span>;
}

const branchOf = (isCloud) => (isCloud === false ? "local" : isCloud === true ? "cloud" : null);

function sanitizeBandwidth(value) {
  if (value === "" || value === null || value === undefined) return null;
  const n = Math.floor(Number(value));
  return Number.isFinite(n) && n >= 0 ? n : null;
}

function BranchCard({ branch, selected, recommended, title, description, icon, onSelect }) {
  const style = {
    cursor: "pointer",
    borderWidth: "1.5px",
    transition: "border-color 0.2s, box-shadow 0.2s, transform 0.05s",
    ...(selected && { borderColor: "var(--primary, #5e72e4)", boxShadow: PRIMARY_HALO }),
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onSelect();
    }
  };

  return (
    <Card
      className="h-100 user-select-none"
      style={style}
      role="radio"
      aria-checked={selected}
      tabIndex={0}
      onClick={onSelect}
      onKeyDown={handleKeyDown}
    >
      <CardBody>
        <div className="d-flex align-items-start justify-content-between">
          <i className={`${icon} fa-2x`} style={{ opacity: selected ? 1 : 0.6 }} />
          <div className="d-flex align-items-center" style={{ gap: "0.4rem" }}>
            {recommended === branch && (
              <Badge color="info" pill>
                <i className="fas fa-star mr-1" />
                Recommended
              </Badge>
            )}
            {selected && (
              <Badge color="primary" pill>
                Selected
              </Badge>
            )}
          </div>
        </div>
        <h5 className="mt-2 mb-1">{title}</h5>
        <small className="text-muted d-block">{description}</small>
      </CardBody>
    </Card>
  );
}

/** @param {import("../WizardCard").StepPanelProps} props */
export default function InstallationInfoPanel({
  installation,
  setField,
  draftFields,
  setDraftField,
  status,
  bottomNav,
}) {
  const wanIpv4 = installation?.wan_ipv4 ?? "";
  const uploadSpeed = installation?.upload_speed ?? "";
  const ispName = installation?.isp_name ?? "";

  const isCloud = installation?.is_cloud;
  const recommendedIsCloud = draftFields?.is_cloud_recommended;

  useEffect(() => {
    if (recommendedIsCloud == null && isCloud != null) {
      setDraftField("is_cloud_recommended", isCloud);
    }
  }, [recommendedIsCloud, isCloud, setDraftField]);

  const recommendedBranch = branchOf(recommendedIsCloud);
  const selectedBranch = branchOf(isCloud) ?? "cloud";

  return (
    <WizardPanel
      title="Installation Information"
      subtitle="Network information collected on-site"
      status={status}
      bottomNav={bottomNav}
    >
      <Row>
        <Col md={6}>
          <PanelField
            label="WAN IPv4"
            mono
            placeholder="e.g. 203.0.113.5"
            value={wanIpv4}
            onChange={(v) => setField("wan_ipv4", v)}
          />
        </Col>
        <Col md={6}>
          <PanelField
            label="Upload bandwidth (Mbps)"
            type="number"
            min={0}
            step={1}
            placeholder="e.g. 50"
            value={uploadSpeed}
            onChange={(v) => setField("upload_speed", sanitizeBandwidth(v))}
          />
        </Col>
        <Col md={6}>
          <PanelField
            label="ISP name"
            placeholder="e.g. Orange"
            value={ispName}
            onChange={(v) => setField("isp_name", v)}
          />
        </Col>
      </Row>

      <FormGroup className="d-flex align-items-center" style={{ gap: "0.75rem" }}>
        <Label className="text-muted small mb-0">CGNAT</Label>
        <CgnatBadge wanIpv4={wanIpv4} />
      </FormGroup>

      <hr style={SECTION_DIVIDER_STYLE} />

      <div className="d-flex align-items-center mb-3" style={{ gap: "0.75rem" }}>
        <span className="font-weight-bold">Installation type</span>
        {recommendedBranch ? (
          <Badge color={recommendedBranch === "cloud" ? "info" : "warning"} pill>
            <i className={`fas fa-${recommendedBranch === "cloud" ? "cloud" : "home"} mr-1`} />
            {recommendedBranch === "cloud" ? "Cloud recommended" : "Local recommended"}
          </Badge>
        ) : (
          <small className="text-muted">Waiting for backend to compute</small>
        )}
      </div>

      <Row role="radiogroup" aria-label="Installation type">
        <Col md={6}>
          <BranchCard
            branch="cloud"
            selected={selectedBranch === "cloud"}
            recommended={recommendedBranch}
            title="Cloud"
            description="Port forwarding + DDNS via router. External RTSP access."
            icon="fas fa-cloud"
            onSelect={() => {
              if (isCloud !== true) setField("is_cloud", true);
            }}
          />
        </Col>
        <Col md={6}>
          <BranchCard
            branch="local"
            selected={selectedBranch === "local"}
            recommended={recommendedBranch}
            title="Local"
            description="Direct Video Recorder access on the local network. No port forwarding."
            icon="fas fa-home"
            onSelect={() => {
              if (isCloud !== false) setField("is_cloud", false);
            }}
          />
        </Col>
      </Row>

      {recommendedBranch && recommendedBranch !== selectedBranch && (
        <small className="d-flex align-items-center mt-3 text-warning" style={{ gap: "0.4rem" }}>
          <i className="fas fa-exclamation-triangle" />
          <span>
            Manual override — recommended was{" "}
            <i className={`fas fa-${recommendedBranch === "cloud" ? "cloud" : "home"} mx-1`} />
            {recommendedBranch === "cloud" ? "Cloud" : "Local"}
          </span>
        </small>
      )}
    </WizardPanel>
  );
}
